/**
 * Se c'è una versione più recente di questa app, e quale.
 *
 * ## Perché serve, e perché non basta «l'ultima è diversa dalla mia»
 *
 * Fuori dagli store nessuno si aggiorna da solo: un apparecchio resta sulla versione con cui è stato
 * installato finché qualcuno non gli dice che ce n'è un'altra. È scritto nel README dell'involucro
 * Android fra le cose che mancano per poterlo distribuire.
 *
 * Il confronto non si fa fra stringhe, ed è la trappola che rende questo file necessario: `'1.9.0' >
 * '1.10.0'` è **vero** in JavaScript, perché confronta carattere per carattere e `9` viene dopo `1`.
 * Un apparecchio sulla 1.10.0 si sentirebbe proporre la 1.9.0 come aggiornamento, e installandola
 * tornerebbe indietro — con l'avviso che ricompare al riavvio, per sempre.
 *
 * ## Si confrontano numeri, e si rinuncia quando non lo sono
 *
 * Una versione che non ha la forma `maggiore.minore.correzione` non si confronta: si risponde «non
 * c'è niente di nuovo». Indovinare l'ordine fra `1.2.3` e `beta-2` significa proporre un
 * aggiornamento a caso, e un aggiornamento a caso è peggio di nessun aggiornamento.
 */

/** Le tre cifre di una versione, o `null` se non ha quella forma. */
function parse(version: string): [number, number, number] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(version.trim());
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

/**
 * Vero se `candidate` viene dopo `current`.
 *
 * Uguali è falso, ed è voluto: reinstallare la versione che si ha già non è un aggiornamento, e
 * proporlo fa sembrare rotto qualcosa che funziona.
 */
export function isNewerVersion(candidate: string, current: string): boolean {
  const a = parse(candidate);
  const b = parse(current);
  if (!a || !b) return false;

  // Destrutturate e non scorse con un indice: `tupla[i]` è `number | undefined` sotto
  // `noUncheckedIndexedAccess`, e i confronti si farebbero fra valori che il compilatore non sa
  // definiti. Tre cifre sono tre righe: un ciclo qui non risparmia niente e perde la garanzia.
  const [majorA, minorA, patchA] = a;
  const [majorB, minorB, patchB] = b;

  if (majorA !== majorB) return majorA > majorB;
  if (minorA !== minorB) return minorA > minorB;
  return patchA > patchB;
}

/**
 * I prodotti del registro dei rilasci che sono app da scaricare.
 *
 * Gli stessi nomi di `license-manager/lib/components.ts`: sono le cartelle sotto `/rilasci/`, quindi
 * sbagliarne uno dà un indice vuoto e nessun aggiornamento, in silenzio.
 */
export type InstalledProduct = 'player-android' | 'player-tizen' | 'player-webos';

/**
 * Qual è il prodotto installato, visto da dentro l'app.
 *
 * ## Perché `browser` non è un prodotto
 *
 * Perché non c'è niente di installato da aggiornare: la pagina si ricarica e **è** già l'ultima
 * versione. Proporre un `.apk` a chi guarda da un browser è offrire un file che non saprebbe dove
 * mettere.
 *
 * Il tipo dell'argomento ripete i quattro nomi di `RemotePlatform` invece di importarlo, perché il
 * dominio non dipende dalle porte. Non è una copia che può scollarsi: il giorno che `RemotePlatform`
 * guadagna una piattaforma, chi chiama questa funzione passandole una `RemotePlatform` **non
 * compila** — ed è lì che va deciso se quella piattaforma ha un artefatto o no.
 */
export function productFor(
  platform: 'browser' | 'androidTv' | 'tizen' | 'webos',
): InstalledProduct | null {
  switch (platform) {
    // Android TV, box AOSP e telefono sono lo stesso APK: l'involucro è uno.
    case 'androidTv':
      return 'player-android';
    case 'tizen':
      return 'player-tizen';
    case 'webos':
      return 'player-webos';
    case 'browser':
      return null;
  }
}

/** Uno dei file di una versione: la stessa app per un'architettura diversa. */
export interface AvailableVariant {
  /** `universal`, `arm64-v8a`, `armeabi-v7a`. */
  readonly variant: string;
  readonly size: number;
  readonly sha256: string;
  readonly url: string;
}

/** Una versione come la pubblica il registro dei rilasci. */
export interface AvailableRelease {
  readonly version: string;
  readonly notes: string;
  /** Dell'universale, cioè del file che si installa su tutto. */
  readonly size: number;
  readonly sha256: string;
  readonly url: string;
  /**
   * Tutti i file di questa versione, l'universale compreso. **Mai vuoto.**
   *
   * Un elemento solo quando il registro non conosce le varianti o quando il prodotto non ne ha:
   * Tizen e webOS non portano codice nativo, un `.wgt` è l'applicazione web confezionata ed è uguale
   * su ogni modello.
   */
  readonly variants: ReadonlyArray<AvailableVariant>;
}

/**
 * Il nome della variante che si installa su qualunque apparecchio.
 *
 * Non è un ripiego: su Android è l'unico file che non possa essere rifiutato per architettura.
 */
export const UNIVERSAL_VARIANT = 'universal';

/**
 * Quale file scaricare, sapendo cosa l'apparecchio sa eseguire.
 *
 * ## Perché non si può sbagliare in silenzio
 *
 * Scaricare la variante sbagliata **non degrada niente: non si installa.** Android rifiuta un
 * pacchetto per un'altra architettura con `INSTALL_FAILED_NO_MATCHING_ABIS`, e su un televisore si
 * legge «app non installata» — senza un motivo, senza un log, e dopo aver scaricato 190 MB.
 *
 * ## L'ordine è quello dell'apparecchio, non quello dell'elenco
 *
 * Si scorre `abis` e per ciascuna si cerca la variante, non il contrario. `Build.SUPPORTED_ABIS` è
 * ordinato **per preferenza del sistema**: su un apparecchio a 64 bit è `[arm64-v8a, armeabi-v7a]`,
 * perché sa eseguire anche il codice a 32 bit. Scorrendo le varianti, su quello stesso apparecchio
 * si prenderebbe l'`armeabi-v7a` se comparisse prima in elenco — un file che si installa, gira, e
 * **non è quello giusto**: codice a 32 bit su una macchina a 64, scelto da un ordinamento che non
 * significa niente.
 *
 * ## Senza corrispondenza si prende l'universale, e si dice
 *
 * È una scelta corretta, non una degradazione mascherata: l'universale contiene tutte le
 * architetture, quindi si installa comunque. Ma vuol dire che quell'apparecchio ha un'architettura
 * per cui non pubblichiamo un file separato, e chi legge i registri deve poterlo sapere — altrimenti
 * ogni apparecchio x86 scaricherebbe per sempre 131 MB in più senza che nessuno se ne accorga.
 *
 * Un elenco di `abis` vuoto porta allo stesso esito: è un apparecchio che non ha saputo dire cosa
 * sa eseguire, e lì l'unica risposta che non può essere sbagliata è quella che funziona su tutto.
 */
export function variantForDevice(
  abis: ReadonlyArray<string>,
  variants: ReadonlyArray<AvailableVariant>,
): { chosen: AvailableVariant; matchedAbi: string | null } | null {
  const universal = variants.find((candidate) => candidate.variant === UNIVERSAL_VARIANT) ?? null;

  for (const abi of abis) {
    const found = variants.find((candidate) => candidate.variant === abi);
    if (found) return { chosen: found, matchedAbi: abi };
  }

  /*
    Niente universale e nessuna corrispondenza: **non si propone niente.**

    Potrebbe esserci un arm64 valido in quell'elenco, e si rinuncia comunque: proporlo a un
    apparecchio che non ha dichiarato di saper eseguire quell'architettura è un aggiornamento che
    fallisce l'installazione. Un avviso che non compare è meglio di un avviso che porta a un errore
    senza spiegazione.
  */
  return universal === null ? null : { chosen: universal, matchedAbi: null };
}

/**
 * La versione da proporre, fra quelle che il registro offre.
 *
 * ## La più recente, non la prima dell'elenco
 *
 * L'indice le restituisce già ordinate, ma fidarsi dell'ordine di una risposta è il modo in cui si
 * propone la versione sbagliata il giorno che qualcuno cambia una clausola `ORDER BY`. Qui si sceglie
 * **confrontando**, che costa nulla su una decina di voci e non dipende da una promessa altrui.
 *
 * ## Tutte restano scaricabili, e questa è solo la proposta
 *
 * Chi guarda l'elenco può prendersi una versione precedente: un apparecchio vecchio può non reggere
 * l'ultima, e lasciarlo senza niente da installare sarebbe peggio che dargli una versione indietro.
 * Questa funzione risponde a «cosa consiglio di installare», non a «cosa può installare».
 */
export function updateToOffer(
  releases: ReadonlyArray<AvailableRelease>,
  currentVersion: string,
): AvailableRelease | null {
  let best: AvailableRelease | null = null;

  for (const release of releases) {
    if (!isNewerVersion(release.version, currentVersion)) continue;
    if (best === null || isNewerVersion(release.version, best.version)) best = release;
  }

  return best;
}

/**
 * Le versioni che l'indice del registro pubblica, o `null` se la risposta non è quella.
 *
 * ## Perché sta nel dominio e non nell'adattatore
 *
 * Perché è una **regola**, non una chiamata: quali campi servono, cosa si fa di una riga storta, e
 * che differenza c'è fra «non c'è niente di nuovo» e «non ho capito la risposta». Nell'adattatore
 * sarebbe mescolata alla rete e si potrebbe provare solo con un finto `fetch`.
 *
 * ## Una riga storta si salta, una risposta storta è `null`
 *
 * Non è la stessa cosa e non va trattata allo stesso modo. Una versione senza numero in un elenco di
 * dieci è un rilascio scritto male: si salta e si offrono le altre nove. Una risposta che non è
 * nemmeno un elenco è un host che non è il registro — un portale di rete chiuso, un proxy che
 * risponde una pagina d'errore — e lì non c'è niente da offrire.
 *
 * `sha256` è obbligatorio e questa è la ragione: è l'unica cosa che lega il file che si scarica al
 * rilascio che il registro dichiara. Una versione senza impronta non è scaricabile, quindi non si
 * offre.
 */
export function parseReleaseIndex(payload: unknown): AvailableRelease[] | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const body = payload as { versions?: unknown };
  if (!Array.isArray(body.versions)) return null;

  const releases: AvailableRelease[] = [];
  for (const entry of body.versions) {
    if (typeof entry !== 'object' || entry === null) continue;
    const row = entry as Record<string, unknown>;
    if (typeof row.version !== 'string' || row.version === '') continue;
    if (typeof row.url !== 'string' || row.url === '') continue;
    if (typeof row.sha256 !== 'string' || row.sha256 === '') continue;

    releases.push({
      version: row.version,
      notes: typeof row.notes === 'string' ? row.notes : '',
      size: typeof row.size === 'number' ? row.size : 0,
      sha256: row.sha256,
      url: row.url,
      variants: parseVariants(row),
    });
  }

  return releases;
}

/**
 * Le varianti di una versione, o l'universale ricavato dai campi in testa.
 *
 * ## Un registro che non le conosce non è una versione senza file
 *
 * Se `variants` manca — l'autenticatore prima del 02-10-2026, o un manifesto firmato prima che le
 * varianti esistessero — si risponde l'universale costruito con `url`, `sha256` e `size` della
 * versione, che è **esattamente cos'era** quel pacchetto: un unico APK con dentro tutte le
 * architetture. Un elenco vuoto farebbe sparire l'aggiornamento invece di offrirlo intero.
 *
 * ## `sha256` obbligatorio anche qui
 *
 * È l'unica cosa che lega il file scaricato al manifesto firmato. Una variante senza impronta si
 * salta: offrirla significherebbe un download che non si può verificare, e su Android la verifica
 * è ciò che sta fra un aggiornamento e un APK altrui.
 */
function parseVariants(row: Record<string, unknown>): AvailableVariant[] {
  const universal: AvailableVariant = {
    variant: UNIVERSAL_VARIANT,
    size: typeof row.size === 'number' ? row.size : 0,
    sha256: row.sha256 as string,
    url: row.url as string,
  };

  if (!Array.isArray(row.variants) || row.variants.length === 0) return [universal];

  const found: AvailableVariant[] = [];
  for (const entry of row.variants) {
    if (typeof entry !== 'object' || entry === null) continue;
    const candidate = entry as Record<string, unknown>;
    if (typeof candidate.variant !== 'string' || candidate.variant === '') continue;
    if (typeof candidate.url !== 'string' || candidate.url === '') continue;
    if (typeof candidate.sha256 !== 'string' || candidate.sha256 === '') continue;
    found.push({
      variant: candidate.variant,
      size: typeof candidate.size === 'number' ? candidate.size : 0,
      sha256: candidate.sha256,
      url: candidate.url,
    });
  }

  // Senza universale fra quelle dichiarate si aggiunge quello della versione: i campi in testa lo
  // descrivono sempre, e senza di lui un apparecchio con un'architettura non prevista resterebbe
  // senza niente da installare.
  return found.some((v) => v.variant === UNIVERSAL_VARIANT) ? found : [universal, ...found];
}
